import React, { useState, useEffect } from 'react';
import { getWebForm } from '../firebase/firestore';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Direct Firestore write — no auth needed, no CRMContext
async function submitLeadPublic(formConfig, formData) {
  await addDoc(collection(db, 'leads'), {
    ...formData,
    // companyId is required — every lead list in the CRM filters by it.
    // Without this, public-form leads were created successfully in Firestore
    // but never matched any company's query, so they silently vanished from
    // every dashboard/leads page even though the submission itself "worked."
    companyId:   formConfig.companyId,
    source:      formConfig.source || 'web_form',
    countries:   formData.country ? [formData.country] : [],
    priority:    'warm',
    assignedTo:  '',
    assignedToId: '',
    score:       Math.floor(Math.random() * 30) + 60,
    formId:      formConfig.id,
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
  });
}

export default function PublicForm({ formId }) {
  const [formConfig, setFormConfig] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [notFound,   setNotFound]   = useState(false);
  const [permError,  setPermError]  = useState(false);
  const [values,     setValues]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [errors,     setErrors]     = useState({});

  useEffect(() => {
    getWebForm(formId).then(snap => {
      if (snap.exists()) {
        const cfg = { id: snap.id, ...snap.data() };
        setFormConfig(cfg);
        // Initialise empty values for each enabled field
        const init = {};
        (cfg.fields || []).filter(f => f.enabled).forEach(f => { init[f.key] = ''; });
        setValues(init);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }).catch((e) => {
      if (e?.code === 'permission-denied') {
        setPermError(true);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    });
  }, [formId]);

  const validate = () => {
    const errs = {};
    (formConfig.fields || []).filter(f => f.enabled && f.required).forEach(f => {
      if (!values[f.key]?.trim()) errs[f.key] = `${f.label} is required`;
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await submitLeadPublic(formConfig, values);
      setSubmitted(true);
    } catch (err) {
      setErrors({ _global: 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const accent = formConfig?.accentColor || '#7c3aed';

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, #7c3aed, #3b82f6)`, margin: '0 auto 14px', animation: 'spin 1s linear infinite' }} />
        <div style={{ fontSize: 13, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Loading form…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (permError) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'Inter, sans-serif', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔐</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Firestore rules not deployed</div>
        <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
          Public access is blocked. Please run this command in your project folder:
        </div>
        <div style={{ margin: '16px 0', padding: '12px 16px', background: '#0f172a', borderRadius: 10, textAlign: 'left' }}>
          <code style={{ fontSize: 13, color: '#a5f3fc', fontFamily: 'monospace' }}>firebase deploy --only firestore:rules</code>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>After deploying, refresh this page.</div>
      </div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'Inter, sans-serif', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🔍</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Form not found</div>
        <div style={{ fontSize: 14, color: '#64748b' }}>This form link may be invalid or has been removed.</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, #f8fafc 0%, ${accent}18 100%)`, fontFamily: 'Inter, sans-serif', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 400, background: '#fff', borderRadius: 20, padding: '40px 36px', boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>{formConfig.thankYouTitle || 'Thank You!'}</div>
        <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{formConfig.thankYouMsg || "We've received your enquiry and will be in touch shortly."}</div>
        <div style={{ marginTop: 28, padding: '10px 16px', background: `${accent}12`, borderRadius: 10, fontSize: 12, color: '#64748b' }}>
          Powered by <strong style={{ color: accent }}>Clarion CRM</strong>
        </div>
      </div>
    </div>
  );

  const enabledFields = (formConfig.fields || []).filter(f => f.enabled);

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, #f8fafc 0%, ${accent}18 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'Inter', sans-serif",
    }}>
      {/* Background blobs */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: `${accent}14`, filter: 'blur(80px)' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 300, height: 300, borderRadius: '50%', background: '#3b82f614', filter: 'blur(60px)' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480 }}>
        {/* Card */}
        <div style={{
          background: '#ffffff', borderRadius: 20, padding: '36px 36px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}>
          {/* Accent line */}
          <div style={{ width: 44, height: 5, borderRadius: 3, background: accent, marginBottom: 20 }} />

          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8, lineHeight: 1.2 }}>
            {formConfig.title}
          </h1>
          {formConfig.subtitle && (
            <p style={{ fontSize: 13.5, color: '#475569', marginBottom: 28, lineHeight: 1.6 }}>
              {formConfig.subtitle}
            </p>
          )}

          <form onSubmit={handleSubmit}>
            {enabledFields.map(field => {
              // Determine the effective type — stored field.type wins; fallback
              // to legacy key-based detection for old forms that didn't store type.
              const ftype = field.type || (
                field.key === 'message' ? 'textarea' :
                field.key === 'phone'   ? 'phone'   :
                field.key === 'email'   ? 'email'   :
                field.key === 'dob'     ? 'date'    :
                field.key === 'budget'  ? 'number'  :
                field.key === 'ielts'   ? 'number'  : 'text'
              );
              const borderColor = errors[field.key] ? '#ef4444' : 'rgba(0,0,0,0.12)';
              const sharedInputStyle = {
                width: '100%', boxSizing: 'border-box',
                border: `1.5px solid ${borderColor}`,
                borderRadius: 10, fontSize: 14, fontFamily: 'inherit',
                outline: 'none', background: '#fafafa', color: '#0f172a',
              };
              const onFocus = e => e.target.style.borderColor = accent;
              const onBlur  = e => e.target.style.borderColor = errors[field.key] ? '#ef4444' : 'rgba(0,0,0,0.12)';
              const onChange = e => {
                const val = ftype === 'checkbox' ? e.target.checked : e.target.value;
                setValues(p => ({ ...p, [field.key]: val }));
                setErrors(p => ({ ...p, [field.key]: '' }));
              };

              let input;
              if (ftype === 'textarea') {
                input = (
                  <textarea rows={3} value={values[field.key] || ''} onChange={onChange}
                    onFocus={onFocus} onBlur={onBlur}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    style={{ ...sharedInputStyle, padding: '10px 14px', resize: 'vertical' }} />
                );
              } else if (ftype === 'select' && field.options?.length) {
                input = (
                  <select value={values[field.key] || ''} onChange={onChange}
                    onFocus={onFocus} onBlur={onBlur}
                    style={{ ...sharedInputStyle, height: 46, padding: '0 14px', cursor: 'pointer', appearance: 'auto' }}>
                    <option value=''>Select {field.label}…</option>
                    {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                );
              } else if (ftype === 'checkbox') {
                input = (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
                    <input type='checkbox' checked={!!values[field.key]} onChange={onChange}
                      style={{ width: 18, height: 18, accentColor: accent, cursor: 'pointer' }} />
                    Yes
                  </label>
                );
              } else {
                // text / number / date / email / phone / url
                const htmlType =
                  ftype === 'phone'  ? 'tel'    :
                  ftype === 'email'  ? 'email'  :
                  ftype === 'number' ? 'number' :
                  ftype === 'date'   ? 'date'   :
                  ftype === 'url'    ? 'url'    : 'text';
                input = (
                  <input type={htmlType} value={values[field.key] || ''} onChange={onChange}
                    onFocus={onFocus} onBlur={onBlur}
                    placeholder={ftype === 'date' ? undefined : `Enter ${field.label.toLowerCase()}`}
                    style={{ ...sharedInputStyle, height: 46, padding: '0 14px' }} />
                );
              }

              return (
                <div key={field.key} style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                    {field.label}
                    {field.required && <span style={{ color: accent, marginLeft: 3 }}>*</span>}
                  </label>
                  {input}
                  {errors[field.key] && (
                    <div style={{ fontSize: 12, color: '#ef4444', marginTop: 5 }}>{errors[field.key]}</div>
                  )}
                </div>
              );
            })}

            {errors._global && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 9, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 16 }}>
                {errors._global}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', height: 50, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
                color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '0.2px',
                marginTop: 8, boxShadow: `0 4px 20px ${accent}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'opacity 0.2s',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                  Submitting…
                </>
              ) : formConfig.submitBtnText || 'Submit Enquiry'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11.5, color: '#94a3b8' }}>
            Powered by <strong style={{ color: accent }}>Clarion</strong> · Your data is secure
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
